import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AboutRoute } from "./routes/about-route";
import { LandingRoute } from "./routes/landing-route";
import { PrivacyRoute } from "./routes/privacy-route";
import { TermsRoute } from "./routes/terms-route";
import { ErrorBoundary } from "./components/error-boundary";
import { AnalyticsProvider } from "./analytics/analytics-provider";
import type { AnalyticsClient } from "./analytics/analytics-client";
import "./styles/global.css";
import {
  RuntimeConfigProvider,
  normalizeRuntimeConfig,
  runtimeConfig as defaultRuntimeConfig,
  type RuntimeConfigInput,
} from "./runtime-config";

const testMode = import.meta.env.MODE === "test";
const eagerRoutes = testMode
  ? await Promise.all([
      import("./routes/check-route"),
      import("./routes/result-route"),
      import("./routes/shared-result-route"),
      import("./routes/pilot-review-route"),
    ])
  : null;
const CheckRoute = testMode
  ? eagerRoutes![0].CheckRoute
  : lazy(() =>
      import("./routes/check-route").then(({ CheckRoute }) => ({
        default: CheckRoute,
      })),
    );
const ResultRoute = testMode
  ? eagerRoutes![1].ResultRoute
  : lazy(() =>
      import("./routes/result-route").then(({ ResultRoute }) => ({
        default: ResultRoute,
      })),
    );
const SharedResultRoute = testMode
  ? eagerRoutes![2].SharedResultRoute
  : lazy(() =>
      import("./routes/shared-result-route").then(({ SharedResultRoute }) => ({
        default: SharedResultRoute,
      })),
    );
const PilotReviewRoute = testMode
  ? eagerRoutes![3].PilotReviewRoute
  : lazy(() =>
      import("./routes/pilot-review-route").then(({ PilotReviewRoute }) => ({
        default: PilotReviewRoute,
      })),
    );

export function App({
  runtimeConfig = defaultRuntimeConfig,
  analytics,
}: Readonly<{
  runtimeConfig?: RuntimeConfigInput;
  analytics?: AnalyticsClient;
}>) {
  const normalizedRuntimeConfig = normalizeRuntimeConfig(runtimeConfig);

  return (
    <RuntimeConfigProvider value={normalizedRuntimeConfig}>
      <AnalyticsProvider
        analytics={analytics}
        runtimeConfig={normalizedRuntimeConfig}
      >
        <ErrorBoundary>
          <Suspense
            fallback={<p aria-live="polite">画面を読み込んでいます…</p>}
          >
            <Routes>
              <Route path="/" element={<LandingRoute />} />
              <Route path="/check" element={<CheckRoute />} />
              <Route path="/result" element={<ResultRoute />} />
              <Route path="/result/shared" element={<SharedResultRoute />} />
              <Route path="/privacy" element={<PrivacyRoute />} />
              <Route path="/terms" element={<TermsRoute />} />
              <Route path="/about" element={<AboutRoute />} />
              <Route path="/pilot/review" element={<PilotReviewRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AnalyticsProvider>
    </RuntimeConfigProvider>
  );
}
